import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBuildingCircleCheck,
    faBuildingCircleXmark,
    faLocationSlash,
    faSpinner,
} from "@fortawesome/sharp-light-svg-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Point } from "geojson";
import { useMemo, useState } from "react";
import {
    type AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    DecisionEnum,
    type UnmatchedDataPointsListRequest,
} from "../../apiClient/generated";
import { usePermission } from "../../auth";
import {
    useDataProvidersApi,
    useGeoDataApi,
    useInfrastructureApiClient,
    useMapApiClient,
    useUnmatchedDataApiClient,
} from "../../hooks";
import { ROUTES } from "../../routes";
import {
    BulkActionsMenu,
    type BulkActionsProps,
    BulkFailuresDownloadButton,
    BulkFailuresPreview,
    BulkMenuItem,
    BulkScopeSelector,
    type FilterSummaryItem,
} from "../../ui/BulkTableAction";
import { PrimaryButton, SecondaryButton } from "../../ui/Buttons";
import { Modal } from "../../ui/Modals";
import { formatDateTime } from "../../utils/dates";
import {
    invalidateKeyPrefix,
    UNMATCHED_TABLE_PREFIX,
} from "../../utils/queryInvalidation";
import { DecisionMinimap } from "./DecisionMinimap";
import { humanizeDecision } from "./decisions";
import { SitePicker } from "./SitePicker";

// Filters come from unmatchedDataPointTableFiltersAtom and are spread into the
// bulk endpoint, so they carry the data point list-request shape.
type DataPointBulkProps = BulkActionsProps<UnmatchedDataPointsListRequest>;

const dataPointFailureLink = (id: string) =>
    `${ROUTES.DASHBOARD_UNMATCHED_DATA}?dataPoint=${id}`;

const IGNORED_FILTER_KEYS: string[] = ["ordering"];

const humanizeFilterKey = (key: string) =>
    key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (character) => character.toUpperCase());

const FILTER_LABELS: Partial<
    Record<keyof UnmatchedDataPointsListRequest, string>
> = {
    bridgerDetectionId: "Bridger Detection ID",
    bridgerEmissionLocationId: "Bridger Emission Location ID",
    bridgerEmitterHeightFtMax: "Bridger Emitter Height (ft) max",
    bridgerEmitterHeightFtMin: "Bridger Emitter Height (ft) min",
    bridgerEquipmentId: "Bridger Equipment ID",
    bridgerEquipmentType: "Bridger Equipment Type",
    bridgerLocation: "Bridger Location",
    bridgerPersistence: "Bridger Persistence",
    bridgerSiteName: "Bridger Site Name",
    createdAtAfter: "Import Date from",
    createdAtBefore: "Import Date to",
    decision: "Action Taken",
    detectedRateMax: "Detection rate max",
    detectedRateMin: "Detection rate min",
    detectionDateRangeAfter: "Detection Date (UTC) from",
    detectionDateRangeBefore: "Detection Date (UTC) to",
    distanceTo: "Distance to",
    locationPreset: "Location",
    locationWithin: "Location",
    providerWithSource: "Provider",
    search: "Search",
};

const formatProviderFilter = (
    value: AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    providerName: string | undefined,
) => {
    const sources = value.se?.filter(Boolean);
    const name = providerName ?? value.id;
    return sources?.length ? `${name} (${sources.join(", ")})` : name;
};

const useFilterSummary = (filters: UnmatchedDataPointsListRequest) => {
    const dataProvidersApiClient = useDataProvidersApi();
    const geoDataApiClient = useGeoDataApi();
    const locationPresetIds = filters.locationPreset ?? [];
    const providerFilters = useMemo(() => {
        if (Array.isArray(filters.providerWithSource)) {
            return filters.providerWithSource;
        }
        if (typeof filters.providerWithSource === "string") {
            try {
                return JSON.parse(filters.providerWithSource);
            } catch {
                return [];
            }
        }
        return [];
    }, [filters.providerWithSource]);
    const providersQuery = useQuery({
        queryKey: ["dataProvidersForBulkFilterSummary"],
        queryFn: () => dataProvidersApiClient.dataProvidersList(),
        enabled: providerFilters.length > 0,
        staleTime: Infinity,
    });
    const locationPresetsQuery = useQuery({
        queryKey: ["geoFilterItems"],
        queryFn: async () => {
            const response = await geoDataApiClient.geodataItemsList({
                pageSize: 1000,
            });
            return response.results;
        },
        enabled: locationPresetIds.length > 0,
        staleTime: Infinity,
    });

    return useMemo(() => {
        const summary: FilterSummaryItem[] = [];
        for (const [key, rawValue] of Object.entries(filters) as Array<
            [keyof UnmatchedDataPointsListRequest, unknown]
        >) {
            const label = FILTER_LABELS[key] ?? humanizeFilterKey(key);
            if (
                IGNORED_FILTER_KEYS.includes(key) ||
                rawValue === undefined ||
                rawValue === null ||
                rawValue === ""
            )
                continue;

            if (key === "decision" && Array.isArray(rawValue)) {
                summary.push({
                    label,
                    value: rawValue
                        .map((decision) => humanizeDecision(String(decision)))
                        .join(", "),
                });
            } else if (key === "providerWithSource") {
                summary.push({
                    label,
                    value: providerFilters
                        .map((provider) =>
                            formatProviderFilter(
                                provider,
                                providersQuery.data?.results.find(
                                    (candidate) => candidate.id === provider.id,
                                )?.name,
                            ),
                        )
                        .join(", "),
                });
            } else if (key === "locationPreset" && Array.isArray(rawValue)) {
                const names = rawValue
                    .map(
                        (id) =>
                            locationPresetsQuery.data?.find(
                                (preset) => preset.id === id,
                            )?.name,
                    )
                    .filter((name): name is string => Boolean(name));
                summary.push({
                    label,
                    value: names.length
                        ? names.join(", ")
                        : rawValue.join(", "),
                });
            } else if (key === "locationWithin") {
                summary.push({ label, value: "Custom area" });
            } else if (
                (key === "detectedRateMin" || key === "detectedRateMax") &&
                typeof rawValue === "number"
            ) {
                // The table converts rates from grams to kg/h for display.
                summary.push({ label, value: String(rawValue / 1000) });
            } else if (rawValue instanceof Date) {
                summary.push({ label, value: formatDateTime(rawValue) });
            } else if (Array.isArray(rawValue)) {
                summary.push({ label, value: rawValue.join(", ") });
            } else {
                summary.push({ label, value: String(rawValue) });
            }
        }
        return summary;
    }, [
        filters,
        locationPresetsQuery.data,
        providerFilters,
        providersQuery.data,
    ]);
};

export interface BulkDecisionModalProps extends DataPointBulkProps {
    decision: DecisionEnum;
    onClose: () => void;
}

const BulkDecisionModal = (props: BulkDecisionModalProps) => {
    const queryClient = useQueryClient();
    const apiClient = useUnmatchedDataApiClient();
    const mapApiClient = useMapApiClient();
    const infrastructureApiClient = useInfrastructureApiClient();

    const decisionLabel = humanizeDecision(props.decision);
    // Only this decision links to a site, so it is the only one that needs the
    // map and the site picker.
    const needsSite = props.decision === DecisionEnum.OwnedAndLinkToSite;
    const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
    const filterSummary = useFilterSummary(props.filters);

    const allSelectedCountQuery = useQuery({
        queryKey: ["UnmatchedDataPoint", props.filters, "count"],
        queryFn: async () => {
            return apiClient.unmatchedDataPointsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    const [applyToAllFiltered, setApplyToAllFiltered] = useState(
        props.allSelected,
    );

    const mapUnmatchedSelectedEmissionsQuery = useQuery({
        queryKey: [
            "UnmatchedDataPoint",
            props.filters,
            applyToAllFiltered,
            props.selectedIds,
            "map",
        ],
        queryFn: async () => {
            if (applyToAllFiltered) {
                return mapApiClient.mapUnmatchedDataPointsList(props.filters);
            }
            return mapApiClient.mapUnmatchedDataPointsList({
                ids: props.selectedIds,
            });
        },
        enabled: needsSite,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
    // The site picker measures against every selected detection.
    const detectionLocations = useMemo(
        (): Point[] =>
            (mapUnmatchedSelectedEmissionsQuery.data?.results ?? []).map(
                (dataPoint) => dataPoint.location as Point,
            ),
        [mapUnmatchedSelectedEmissionsQuery.data],
    );

    const selectedSiteQuery = useQuery({
        queryKey: ["unmatchedBulkSelectedSite", selectedSiteId],
        enabled: !!selectedSiteId,
        queryFn: async () => {
            const response =
                await infrastructureApiClient.infrastructureSitesList({
                    ids: [selectedSiteId],
                });
            return response.results[0] ?? null;
        },
    });

    const affectedCount = applyToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [errorMessage, setErrorMessage] = useState<string>(null);

    const bulkActionMutation = useMutation({
        mutationFn: async () => {
            return apiClient.unmatchedDataPointsBulkSaveDecisionCreate({
                bulkUpdateUnmatchedDataPointsRequest: {
                    decision: props.decision,
                    ...(needsSite ? { site: selectedSiteId } : {}),
                },
                ...(applyToAllFiltered
                    ? props.filters
                    : { ids: props.selectedIds }),
            });
        },
        onSuccess: () => {
            invalidateKeyPrefix(queryClient, UNMATCHED_TABLE_PREFIX);
            queryClient.invalidateQueries({
                queryKey: ["UnmatchedDataPoint"],
            });
            props.onFinalizeAction();
        },
        onError: async (error: any) => {
            try {
                const body = await error.response?.json();
                setErrorMessage(
                    body?.error ?? "Something went wrong. Please try again.",
                );
            } catch {
                setErrorMessage("Something went wrong. Please try again.");
            }
        },
    });

    // Either the number of affected data points, or a spinner while the count
    // for "all filtered" is still loading.
    const affectedCountDisplay = affectedCount ?? (
        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
    );

    return (
        <Modal
            visible={true}
            onClose={bulkActionMutation.isPending ? undefined : props.onClose}
            size={props.decision === "OWNED_AND_LINK_TO_SITE" ? "md" : "sm"}
        >
            <h2 className="text-lg font-semibold mb-4 mt-2">{decisionLabel}</h2>
            {bulkActionMutation.isIdle && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Are you sure you want to mark {affectedCountDisplay}{" "}
                        data point
                        {affectedCount !== 1 ? "s" : ""} as "{decisionLabel}"?
                    </p>
                    {props.allSelected && (
                        <div className="mt-4">
                            <BulkScopeSelector
                                allSelected={applyToAllFiltered}
                                allSelectedCount={
                                    allSelectedCountQuery.data?.count
                                }
                                filterSummary={filterSummary}
                                noun="data points"
                                onChange={setApplyToAllFiltered}
                                selectedCount={props.selectedIds.length}
                            />
                        </div>
                    )}
                    {needsSite && (
                        <div className="flex flex-col gap-3 mt-4">
                            <div className="flex items-baseline justify-between gap-2">
                                <p className="text-sm font-semibold text-ae-slate-900">
                                    Selected detections
                                </p>
                                <p className="text-xs text-neutral-500">
                                    Click a site to select it
                                </p>
                            </div>
                            <DecisionMinimap
                                mapId="unmatched-bulk-decision"
                                dataPoints={
                                    mapUnmatchedSelectedEmissionsQuery.data
                                        ?.results ?? []
                                }
                                selectedSite={selectedSiteQuery.data ?? null}
                                onSelectSite={setSelectedSiteId}
                                className="h-[300px]"
                            />
                            {!!detectionLocations.length && (
                                <SitePicker
                                    detectionLocations={detectionLocations}
                                    selectedSite={
                                        selectedSiteQuery.data ?? null
                                    }
                                    onSelect={(site) =>
                                        setSelectedSiteId(site.id)
                                    }
                                />
                            )}
                        </div>
                    )}
                    <div className="flex gap-2 mt-5">
                        <PrimaryButton
                            type="button"
                            disabled={
                                bulkActionMutation.isPending ||
                                (allSelectedCountQuery.isLoading &&
                                    props.allSelected) ||
                                (needsSite && !selectedSiteId)
                            }
                            onClick={() => bulkActionMutation.mutateAsync()}
                        >
                            Mark {affectedCountDisplay} data point
                            {affectedCount !== 1 ? "s" : ""}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            disabled={bulkActionMutation.isPending}
                            onClick={props.onClose}
                        >
                            Cancel
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isPending && (
                <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin h-6"
                    />
                    Saving decision on data points...
                </div>
            )}
            {bulkActionMutation.isSuccess && (
                <>
                    <p className="text-sm mb-1">
                        Successfully marked {bulkActionMutation.data.ok} data
                        point
                        {bulkActionMutation.data.ok !== 1 ? "s" : ""} as "
                        {decisionLabel}".
                    </p>
                    {bulkActionMutation.data.failed > 0 && (
                        <>
                            <p className="text-sm text-gray-600 mb-1">
                                {bulkActionMutation.data.failed} data point
                                {bulkActionMutation.data.failed !== 1
                                    ? "s"
                                    : ""}{" "}
                                could not be updated. Download the CSV to see
                                the reason for each failure.
                            </p>
                            <BulkFailuresPreview
                                linkForId={dataPointFailureLink}
                                result={bulkActionMutation.data}
                            />
                        </>
                    )}
                    <div className="flex gap-2 mt-4">
                        <BulkFailuresDownloadButton
                            linkForId={dataPointFailureLink}
                            result={bulkActionMutation.data}
                            filenamePrefix="bulk-decision"
                        />
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isError && (
                <>
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-bold">Error:</span>{" "}
                        {errorMessage ??
                            "Something went wrong. Please try again."}
                    </p>
                    <div className="flex gap-2 mt-4">
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

export const BulkActions = (props: DataPointBulkProps) => {
    const canEdit = usePermission("unmatchedEmissionEdit");
    const [showBulkModal, setShowBulkModal] = useState<DecisionEnum | null>(
        null,
    );

    const bulkActionsDisabled = props.selectedIds.length === 0 || !canEdit;
    const noPermissionTitle =
        "You don't have permission to take decisions on unmatched data points.";

    return (
        <>
            <BulkActionsMenu disabled={bulkActionsDisabled}>
                <BulkMenuItem
                    icon={faBuildingCircleXmark}
                    label={humanizeDecision(DecisionEnum.NotOwnedByCompany)}
                    enabled={canEdit}
                    disabledTitle={noPermissionTitle}
                    onClick={() =>
                        setShowBulkModal(DecisionEnum.NotOwnedByCompany)
                    }
                />
                <BulkMenuItem
                    icon={faBuildingCircleCheck}
                    label={humanizeDecision(DecisionEnum.OwnedAndLinkToSite)}
                    enabled={canEdit}
                    disabledTitle={noPermissionTitle}
                    onClick={() =>
                        setShowBulkModal(DecisionEnum.OwnedAndLinkToSite)
                    }
                />
                <BulkMenuItem
                    icon={faLocationSlash}
                    label={humanizeDecision(DecisionEnum.OwnedButNoSite)}
                    enabled={canEdit}
                    disabledTitle={noPermissionTitle}
                    onClick={() =>
                        setShowBulkModal(DecisionEnum.OwnedButNoSite)
                    }
                />
            </BulkActionsMenu>
            {showBulkModal !== null && (
                <BulkDecisionModal
                    {...props}
                    decision={showBulkModal}
                    onClose={() => setShowBulkModal(null)}
                />
            )}
        </>
    );
};
